/**
 * Клиенты: every client in one table — how far each has come from invitation
 * to trading, whether its exchange link holds, and its money — with one button
 * to add the next one.
 *
 * One table rather than a directory and an operations summary one under the
 * other: the same client listed twice read as two clients.
 */
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, ExternalLink, TriangleAlert, UserPlus } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/design-system/Button";
import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import { Stat } from "@/design-system/Stat";
import { StatusBadge } from "@/design-system/StatusBadge";
import { TextField } from "@/design-system/TextField";
import { createOwnAccount, type ClientOperationsResponse, type PlatformClientResponse } from "@/shared/api";
import { ACCESS } from "@/shared/clients/access";
import { CLIENTS_KEY, useClients } from "@/shared/clients/directory";
import { useClientOperations } from "@/shared/clients/operations";
import { useOwnAccountKeys } from "@/shared/clients/ownAccounts";
import { ClientProgress } from "@/shared/clients/ClientProgress";
import { incomeNote, MARGIN_DANGER, MARGIN_WARNING, periodProfit, platformIncome, PROOF_FRESH_SECONDS, RANK_LABELS, ranked, roster, STAGES, type ClientRow, type RankBy } from "@/shared/clients/roster";
import { atLeast, isNegative, isZero, money, percent, signedMoney, signedPercent, sumDecimal } from "@/shared/money/money";
import { PeriodControl } from "@/shared/period/PeriodControl";
import { periodLabel, readPeriod, writePeriod, type SelectedPeriod } from "@/shared/period/period";
import { SCOPES_KEY, useScopes } from "@/shared/session/scopes";
import { startViewAs } from "@/shared/session/viewAs";
import { plural } from "@/shared/text/plural";

import { AddClientDialog } from "./AddClientDialog";
import { ClientFee } from "./ClientFee";
import { ClientPasswordReset } from "./ClientPasswordReset";
import { ClientReferrer } from "./ClientReferrer";
import { OwnInvitation } from "./OwnInvitation";
import { ReferralPayouts } from "./ReferralPayouts";
import { UnattributedDeposits } from "./UnattributedDeposits";

function usdt(value: string | null | undefined): string {
  if (value == null) return "—";
  return `${money(value)} USDT`;
}

function day(value: string): string {
  return new Date(value).toLocaleString("ru-RU", { dateStyle: "medium" });
}

function Link({ row }: { readonly row: ClientRow }): React.JSX.Element {
  const item = row.operations;
  if (item === undefined || !item.bybit_connected) return <span className="text-text-muted">—</span>;
  const age = item.proof_age_seconds;
  if (age === null) return <StatusBadge tone="danger">не подтверждена</StatusBadge>;
  if (age <= PROOF_FRESH_SECONDS) return <StatusBadge tone="ready">в порядке</StatusBadge>;
  return <StatusBadge tone="danger">{`нет ${String(Math.floor(age / 60))} мин`}</StatusBadge>;
}


function tone(value: string): string {
  if (isZero(value)) return "text-text-primary";
  return isNegative(value) ? "text-status-negative" : "text-status-positive";
}

/** Margin load first: Bybit liquidates the account at 100 %. */
function Risk({ item }: { readonly item: ClientRow["operations"] }): React.JSX.Element {
  if (item?.margin_load == null) return <span className="text-text-muted">—</span>;
  const load = item.margin_load;
  const level = atLeast(load, MARGIN_DANGER) ? "danger" : atLeast(load, MARGIN_WARNING) ? "warning" : "ready";
  return (
    <span className="flex flex-col items-start gap-1 whitespace-nowrap">
      <span title="Нагрузка на маржу: при 100 % Bybit ликвидирует счёт">
        <StatusBadge tone={level}>{`маржа ${percent(load, 0)}`}</StatusBadge>
      </span>
      {item.open_positions > 0 && item.worst_position != null && (
        <span className="text-helper-sm text-text-muted" title="Худшая открытая позиция: цена против средней цены входа">
          худшая позиция {signedPercent(item.worst_position, 0)}
        </span>
      )}
    </span>
  );
}

/** The prepaid fee balance: what is left, how much of it is the welcome
 *  credit, and what was charged. The credit is spent first and is no income. */
function FeeCell({ row }: { readonly row: ClientRow }): React.JSX.Element {
  const item = row.operations;
  if (row.own) return <Figure main="—" note="свой счёт" />;
  if (item?.fee_balance == null) {
    return <Figure main="—" note={row.client.fee_rate == null ? "ставка не задана" : `ставка ${percent(row.client.fee_rate, 0)}`} />;
  }
  const balance = item.fee_balance;
  const level = item.fee_exhausted ? "text-status-negative" : item.fee_low ? "text-status-warning" : "text-text-primary";
  const bonus = item.fee_bonus_left == null || isZero(item.fee_bonus_left) ? null : `бонус ${money(item.fee_bonus_left)}`;
  const owed = item.fee_owed == null || isZero(item.fee_owed) ? null : `долг ${money(item.fee_owed)}`;
  return (
    <Figure
      className={level}
      main={money(balance)}
      note={[owed, bonus, `начислено ${money(item.platform_fee)}`, row.client.fee_rate == null ? null : `ставка ${percent(row.client.fee_rate, 0)}`].filter(Boolean).join(" · ")}
    />
  );
}

/** Under two weeks a monthly figure stands on few days: it says how many
 *  (owner, 2026-10-29: every account's trend, its base in sight). */
const PROJECTION_DAYS = 14;

function resultNote(period: NonNullable<ClientOperationsResponse["period"]>): string {
  const parts = [`${String(period.cycles)} ${plural(period.cycles, "цикл", "цикла", "циклов")}`];
  if (period.win_rate != null) parts.push(`${percent(period.win_rate, 0)} в плюс`);
  const days = period.traded_days == null ? null : Math.max(1, Math.floor(period.traded_days));
  const short = days !== null && days < PROJECTION_DAYS;
  if (period.monthly != null) parts.push(`≈ ${signedPercent(period.monthly)} в мес.${short ? ` (по ${String(days)} дн.)` : ""}`);
  else if (days !== null) parts.push(`торгует ${String(days)} дн.`);
  return parts.join(" · ");
}

function hours(value: number): string {
  return value < 48 ? `${value.toLocaleString("ru-RU")} ч` : `${(value / 24).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} дн.`;
}

/** Two lines at most: the figure, and what it stands on. */
function Figure({ main, note, className = "" }: { readonly main: React.ReactNode; readonly note?: React.ReactNode; readonly className?: string }): React.JSX.Element {
  return (
    <span className="flex flex-col items-end">
      <span className={className}>{main}</span>
      {note != null && <span className="text-helper-sm text-text-muted">{note}</span>}
    </span>
  );
}

function Details({ row, others }: { readonly row: ClientRow; readonly others: readonly PlatformClientResponse[] }): React.JSX.Element {
  const { client, operations } = row;
  return (
    <div className="grid gap-4 bg-background-base px-4 py-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="client-details">
      <div className="flex flex-col gap-1">
        <span className="text-helper-sm text-text-muted">Логин клиента</span>
        <span className="font-mono text-data-mono text-text-primary">{client.owner_login ?? "—"}</span>
        {!row.own && <span className="text-helper-sm text-text-muted">добавлен {day(client.created_at)}</span>}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-helper-sm text-text-muted">Доступ</span>
        <span><StatusBadge tone={ACCESS[client.access].tone}>{ACCESS[client.access].text}</StatusBadge></span>
        {!row.own && client.access === "ACTIVE" && <ClientPasswordReset client={client} />}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-helper-sm text-text-muted">Комиссия платформы с прибыли</span>
        {row.own ? <span className="text-text-primary">0 % — свой счёт</span> : <ClientFee client={client} />}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-helper-sm text-text-muted">Просадка капитала за 30 дней</span>
        <span className="font-numeric tabular-nums text-text-primary">
          {operations?.drawdown == null ? "—" : percent(operations.drawdown, 1)}
        </span>
        <span className="text-helper-sm text-text-muted">
          {operations === undefined ? "" : `зависших позиций (дольше 7 дней): ${operations.stuck_positions == null ? "—" : String(operations.stuck_positions)}`}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-helper-sm text-text-muted">База доходности за период</span>
        <span className="font-numeric tabular-nums text-text-primary">{usdt(operations?.period?.invested)}</span>
        <span className="text-helper-sm text-text-muted">вложено за период, на эту сумму считается доходность</span>
      </div>
      <div className="flex flex-col gap-1" data-testid="client-trading">
        <span className="text-helper-sm text-text-muted">Как торговал за период</span>
        <span className="font-numeric tabular-nums text-text-primary">
          {operations?.period?.average_net == null ? "—" : `${signedMoney(operations.period.average_net)} USDT за сделку`}
        </span>
        <span className="text-helper-sm text-text-muted">
          {[
            operations?.period?.cycles_per_day == null ? null : `${operations.period.cycles_per_day.toLocaleString("ru-RU")} сделок в день`,
            operations?.period?.median_cycle_hours == null ? null : `сделка обычно ${hours(operations.period.median_cycle_hours)}`,
            operations?.period?.drawdown == null ? null : `просадка ${percent(operations.period.drawdown, 1)}`,
          ].filter(Boolean).join(" · ") || "нет закрытых сделок"}
        </span>
      </div>
      {!row.own && (
        <div className="flex flex-col gap-1">
          <span className="text-helper-sm text-text-muted">Пригласил</span>
          <ClientReferrer client={client} operations={operations} others={others} />
        </div>
      )}
      <div className="flex flex-col gap-1">
        <span className="text-helper-sm text-text-muted">Прибыль к выводу</span>
        <span className="font-numeric tabular-nums text-text-primary">{usdt(operations?.to_withdraw)}</span>
      </div>
    </div>
  );
}

/** Five columns on a wide screen, a card per client below it: nothing scrolls
 *  sideways (owner, 2026-10-15). */
const GRID = "grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-[minmax(15rem,1.6fr)_repeat(4,minmax(0,1fr))] lg:items-start";

/** The column's name, said inside the cell on a card, where no header row is. */
function Label({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return <span className="text-label-caps text-text-muted lg:hidden">{children}</span>;
}

function Cell({ label, testId, children }: { readonly label: string; readonly testId?: string; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 lg:block lg:text-right" data-testid={testId}>
      <Label>{label}</Label>
      <div className="font-numeric tabular-nums">{children}</div>
    </div>
  );
}

function RankControl({ by, onBy }: { readonly by: RankBy; readonly onBy: (next: RankBy) => void }): React.JSX.Element {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="rank-control">
      <span className="text-helper-sm text-text-muted">Рейтинг по</span>
      <div role="group" aria-label="Рейтинг счетов" className="inline-flex w-fit max-w-full flex-wrap gap-0.5 rounded-row bg-background-base p-0.5">
        {(Object.keys(RANK_LABELS) as RankBy[]).map((id) => (
          <Button
            key={id}
            variant="ghost"
            size="sm"
            selected={by === id}
            aria-pressed={by === id}
            onClick={() => { onBy(id); }}
            className="h-8 px-3 text-label-caps pointer-coarse:h-11"
          >
            {RANK_LABELS[id]}
          </Button>
        ))}
      </div>
    </div>
  );
}

function ClientTable({ rows, by }: { readonly rows: readonly ClientRow[]; readonly by: RankBy }): React.JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  const items = rows.flatMap((row) => (row.operations === undefined ? [] : [row.operations]));
  const known = <T,>(source: readonly ClientOperationsResponse[], pick: (item: ClientOperationsResponse) => T | null | undefined): T[] =>
    source.flatMap((item) => {
      const value = pick(item);
      return value == null ? [] : [value];
    });
  /** A sum over the listed accounts that know the figure; the rest are named, never read as zero. */
  const total = (source: readonly ClientOperationsResponse[], pick: (item: ClientOperationsResponse) => string | null | undefined, show: (sum: string) => string): React.ReactNode => {
    const values = known(source, pick);
    const missing = source.length - values.length;
    const sum = sumDecimal(values);
    return <Figure className={tone(sum)} main={show(sum)} note={missing > 0 ? `без ${String(missing)}` : undefined} />;
  };
  // Only fee-paying accounts count toward income: one's own pays none and is
  // not "missing" from it.
  const paying = items.filter((item) => item.fee_balance != null);
  return (
    <div className="flex flex-col" data-testid="client-list">
      <div className={`${GRID} hidden border-b border-border-subtle pb-2 text-label-caps text-text-muted lg:grid`}>
        <span>Клиент</span>
        <span className="text-right">Капитал, USDT</span>
        <span className="text-right">Результат за период</span>
        <span>Риск</span>
        <span className="text-right">Баланс комиссии</span>
      </div>
      {ranked(rows, by).map(({ row, place }) => {
        const expanded = open === row.key;
        const item = row.operations;
        const period = item?.period ?? null;
        return (
          <div key={row.key} className="border-b border-border-subtle" data-access={row.client.access} data-testid="client-row">
            <div
              className={`${GRID} cursor-pointer py-3 transition-colors duration-150 hover:bg-surface-hover`}
              onClick={() => { setOpen(expanded ? null : row.key); }}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="-ml-2 h-auto min-w-0 gap-2 py-1 text-left"
                    aria-expanded={expanded}
                    onClick={(event) => { event.stopPropagation(); setOpen(expanded ? null : row.key); }}
                  >
                    {expanded
                      ? <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-text-muted" />
                      : <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-text-muted" />}
                    <span className="flex min-w-0 flex-col">
                      <span className="flex flex-wrap items-center gap-2 font-medium text-text-primary">
                        {place !== null && (
                          <span className="font-numeric tabular-nums text-text-muted" data-testid="client-place" title={`Место по показателю «${RANK_LABELS[by]}» за период`}>
                            {`№${String(place)}`}
                          </span>
                        )}
                        {row.client.display_name}
                        {row.own && <StatusBadge tone="muted" shape="outlined">мой счёт</StatusBadge>}
                      </span>
                      <span className="break-all text-helper-sm text-text-muted">{row.client.owner_login ?? ""}</span>
                    </span>
                  </Button>
                  {!row.own && row.client.access === "ACTIVE" && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Открыть кабинет: ${row.client.display_name}`}
                      title="Открыть кабинет клиента"
                      onClick={(event) => {
                        event.stopPropagation();
                        startViewAs({ tenantId: row.client.tenant_id, accountId: row.client.account_id, name: row.client.display_name });
                      }}
                    >
                      <ExternalLink aria-hidden="true" className="size-4" />
                    </Button>
                  )}
                </div>
                <span className="flex flex-wrap items-center gap-2">
                  <ClientProgress row={row} />
                  <Link row={row} />
                </span>
              </div>
              <Cell label="Капитал, USDT" testId="client-equity">
                <Figure
                  main={item?.equity == null ? "—" : money(item.equity)}
                  note={item === undefined ? undefined : (
                    <span data-testid="client-open">
                      {`${String(item.open_positions)} поз.${item.trailing > 0 ? ` · трейлинг ${String(item.trailing)}` : ""}`}
                      {item.unrealised != null && <span className={tone(item.unrealised)} title="Результат открытых позиций сейчас">{` · ${signedMoney(item.unrealised)}`}</span>}
                    </span>
                  )}
                />
              </Cell>
              <Cell label="Результат за период" testId="client-profit">
                {period === null ? "—" : (
                  <Figure
                    className={tone(period.realised)}
                    main={
                      <>
                        {signedMoney(period.realised)}
                        {period.rate != null && <span data-testid="client-return">{` (${signedPercent(period.rate)})`}</span>}
                      </>
                    }
                    note={resultNote(period)}
                  />
                )}
              </Cell>
              <div className="flex items-start justify-between gap-3 lg:block" data-testid="client-risk">
                <Label>Риск</Label>
                <Risk item={item} />
              </div>
              <Cell label="Баланс комиссии" testId="client-fee-balance"><FeeCell row={row} /></Cell>
            </div>
            {expanded && (
              <div className="border-t border-border-subtle">
                <Details
                  row={row}
                  others={rows.filter((other) => !other.own && other.key !== row.key).map((other) => other.client)}
                />
              </div>
            )}
          </div>
        );
      })}
      {items.length > 1 && (
        <div className={`${GRID} py-3 font-medium`} data-testid="client-totals">
          <span>Итого</span>
          <Cell label="Капитал, USDT" testId="total-equity">
            {total(items, (item) => item.equity, money)}
            <span className="block text-helper-sm text-text-muted" data-testid="total-open">
              открытые {signedMoney(sumDecimal(known(items, (item) => item.unrealised)))}
            </span>
          </Cell>
          <Cell label="Результат за период" testId="total-profit">{total(items, (item) => item.period?.realised, signedMoney)}</Cell>
          <span className="hidden lg:block" />
          <Cell label="Доход платформы" testId="total-fee-income">{total(paying, (item) => item.fee_paid, money)}</Cell>
        </div>
      )}
    </div>
  );
}

/** Figures only from a summary that answered; otherwise a dash with why, never a zero. */
function Summary({
  rows,
  operations,
  period,
}: {
  readonly rows: readonly ClientRow[];
  readonly operations: readonly ClientOperationsResponse[] | undefined;
  readonly period: SelectedPeriod;
}): React.JSX.Element {
  const attention = rows.filter((row) => row.attention.length > 0).length;
  if (operations === undefined) {
    return (
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="clients-summary">
        {["Торгуют", "Прибыль клиентов за период", "Доход платформы", "Требуют внимания"].map((label) => (
          <Stat key={label} label={label} value="—" note="сводка по счетам недоступна" />
        ))}
      </div>
    );
  }
  const trading = rows.filter((row) => row.reached === STAGES.length).length;
  const profit = periodProfit(operations);
  const income = platformIncome(operations);
  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="clients-summary">
      <Stat label="Торгуют" value={`${String(trading)} из ${String(rows.length)}`} note="счетов" />
      <Stat
        label="Прибыль клиентов за период"
        value={usdt(profit.total)}
        note={profit.pending > 0 ? `без ${String(profit.pending)} — ещё считается` : `закрытые циклы, ${periodLabel(period)}`}
      />
      <Stat
        label="Доход платформы"
        value={usdt(income.paid)}
        note={incomeNote(income)}
      />
      <Stat
        label="Требуют внимания"
        value={<span className={attention > 0 ? "text-status-negative" : ""}>{attention}</span>}
        note={attention > 0 ? "см. список ниже" : "всё в порядке"}
      />
    </div>
  );
}

function AttentionList({ rows }: { readonly rows: readonly ClientRow[] }): React.JSX.Element | null {
  const items = rows.flatMap((row) => row.attention.map((item) => ({ row, item })));
  if (items.length === 0) return null;
  return (
    <Panel title="Требует внимания" testId="clients-attention">
      <ul className="flex flex-col gap-2">
        {items.map(({ row, item }) => (
          <li key={`${row.key}-${item.text}`} className="flex items-start gap-2 text-body-main">
            <TriangleAlert aria-hidden="true" className={`mt-0.5 size-4 shrink-0 ${item.tone === "danger" ? "text-status-negative" : "text-status-warning"}`} />
            <span><span className="font-medium text-text-primary">{row.client.display_name}</span> — {item.text}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function OwnAccount(): React.JSX.Element | null {
  const scopes = useScopes();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  if (scopes.some((option) => option.kind === "ACCOUNT")) return null;
  return (
    <Panel title="Мой торговый счёт">
      <form
        className="grid max-w-xl gap-4"
        data-testid="own-account-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (pending) return;
          const form = event.currentTarget;
          const fields = new FormData(form);
          const text = (name: string): string => {
            const value = fields.get(name);
            return typeof value === "string" ? value : "";
          };
          setPending(true);
          setRefusal(null);
          void createOwnAccount({ body: { display_name: text("name"), current_password: text("password") } })
            .then(async (result) => {
              const status = result.response?.status;
              if (status !== 201) {
                setRefusal(status === 403 ? "Пароль администратора не подошёл." : status === 409 ? "Твой счёт уже есть." : "Счёт не создан. Попробуй снова.");
                return;
              }
              form.reset();
              await queryClient.invalidateQueries({ queryKey: SCOPES_KEY });
              await queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
            })
            .catch(() => { setRefusal("Счёт не создан. Попробуй снова."); })
            .finally(() => { setPending(false); });
        }}
      >
        <p className="text-body-main text-text-secondary">
          Счёт под твоим же логином, чтобы торговать самому. Подключение биржи и запуск — отдельно, внутри счёта.
        </p>
        <TextField label="Название счёта" name="name" required maxLength={128} defaultValue="Мой счёт" autoComplete="off" />
        <TextField label="Подтверди паролем администратора" name="password" type="password" required autoComplete="current-password" />
        <div>
          <Button type="submit" variant="primary" disabled={pending}>
            {pending ? "Создаю…" : "Создать мой торговый счёт"}
          </Button>
        </div>
        {refusal !== null && <p role="alert" className="text-sm text-status-negative">{refusal}</p>}
      </form>
    </Panel>
  );
}

/** `adminLogin`: the signed-in administrator, for the browser's saved credentials. */
export function ClientsPage({ adminLogin }: { readonly adminLogin: string }): React.JSX.Element {
  const [adding, setAdding] = useState(false);
  const clients = useClients();
  const [search, setSearch] = useSearchParams();
  const period = readPeriod(search);
  const operations = useClientOperations(period);
  const rank = search.get("rank");
  const by: RankBy = rank === "realised" || rank === "equity" ? rank : "rate";
  const ownKeys = useOwnAccountKeys();
  const rows = clients.data === undefined ? [] : roster(clients.data, operations.data, ownKeys);

  return (
    <div className="flex flex-col gap-2" data-testid="platform-clients">
      <PageHeader
        title="Клиенты"
        description="Кто пользуется платформой, на каком он этапе и как работает его счёт."
        actions={
          <span className="flex flex-wrap items-center gap-2">
            <OwnInvitation />
            <Button type="button" variant="primary" onClick={() => { setAdding(true); }}>
              <UserPlus aria-hidden="true" className="size-4" />
              Добавить клиента
            </Button>
          </span>
        }
      />
      {clients.isPending ? (
        <Panel><p className="text-text-muted">Загружаю…</p></Panel>
      ) : clients.isError ? (
        <Panel><p role="alert" className="text-status-warning">Список недоступен — состояние неизвестно. Обнови страницу.</p></Panel>
      ) : (
        <>
          <PeriodControl
            period={period}
            effectiveSince={operations.data?.find((item) => item.period != null)?.period?.since ?? null}
            effectiveUntil={operations.data?.find((item) => item.period != null)?.period?.until ?? null}
            onPeriod={(next: SelectedPeriod) => {
              // The rating survives a change of period.
              const params = writePeriod(next);
              if (rank !== null) params.set("rank", rank);
              setSearch(params, { replace: false });
            }}
          />
          <Summary rows={rows} operations={operations.data} period={period} />
          <AttentionList rows={rows} />
          <ReferralPayouts />
          <UnattributedDeposits />
          <Panel
            title={`Все клиенты · ${String(rows.length)}`}
            testId="client-table"
            trailing={operations.data === undefined ? undefined : (
              <span className="text-helper-sm text-text-secondary" data-testid="clients-capital">
                капитал на биржах: {usdt(sumDecimal(operations.data.flatMap((item) => (item.equity == null ? [] : [item.equity]))))}
              </span>
            )}
          >
            {operations.isError && (
              <p role="alert" className="mb-3 text-status-warning">Сводка по счетам недоступна — связь и деньги сейчас неизвестны.</p>
            )}
            {rows.length === 0 ? (
              <div className="flex flex-col items-start gap-3 py-2" data-testid="client-list">
                <p className="text-body-main text-text-secondary">
                  Клиентов пока нет. Нажми «Добавить клиента» — получишь ссылку-приглашение для него. Это не подключает биржу и не запускает торговлю.
                </p>
              </div>
            ) : (
              <>
                <RankControl
                  by={by}
                  onBy={(next) => {
                    const params = new URLSearchParams(search);
                    params.set("rank", next);
                    setSearch(params, { replace: true });
                  }}
                />
                <ClientTable rows={rows} by={by} />
              </>
            )}
          </Panel>
          <OwnAccount />
        </>
      )}
      {adding && (
        <AddClientDialog
          adminLogin={adminLogin}
          onClose={() => { setAdding(false); }}
        />
      )}
    </div>
  );
}
